import { ParsedUrlQuery } from 'querystring';
import { PathSessionType } from './types';

const PATH_SESSION_TYPES: readonly PathSessionType[] = [
  'vocab',
  'pronunciation',
  'sentence',
  'aiConversation',
  'immersion',
  'grammar',
  'conjugation',
  'review',
];

export const isPathSessionType = (value: unknown): value is PathSessionType =>
  typeof value === 'string' && (PATH_SESSION_TYPES as readonly string[]).includes(value);

/**
 * Carried from an end-of-session upsell into the plans page, so a learner who leaves the
 * plans page without upgrading is taken into their next path session instead of the
 * dashboard.
 */
export type ContinueOnPathQuery = {
  continueOnPath: 'true';
  pathwayNodeId?: string;
  sessionType?: PathSessionType;
};

export const buildContinueOnPathQuery = (
  pathwayNodeId: number | null,
  sessionType?: PathSessionType | null,
): ContinueOnPathQuery => ({
  continueOnPath: 'true',
  ...(pathwayNodeId !== null && { pathwayNodeId: String(pathwayNodeId) }),
  ...(sessionType ? { sessionType } : {}),
});

/**
 * Re-emits the current page's continue-on-path params, so a link that re-opens the plans
 * page (e.g. the downsell toast) keeps sending the learner on to their next session.
 */
export const getContinueOnPathQueryFromLocation = (): ContinueOnPathQuery | undefined => {
  const continueOnPath = parseContinueOnPathQuery(
    Object.fromEntries(new URLSearchParams(window.location.search)),
  );

  return continueOnPath
    ? buildContinueOnPathQuery(continueOnPath.pathwayNodeId, continueOnPath.sessionType)
    : undefined;
};

/** Returns null unless the query asks to continue on the path. */
export const parseContinueOnPathQuery = (
  query: ParsedUrlQuery,
): { pathwayNodeId: number | null; sessionType: PathSessionType | null } | null => {
  if (query.continueOnPath !== 'true') return null;

  const pathwayNodeId = Number(query.pathwayNodeId);

  return {
    pathwayNodeId:
      typeof query.pathwayNodeId === 'string' && Number.isInteger(pathwayNodeId)
        ? pathwayNodeId
        : null,
    sessionType: isPathSessionType(query.sessionType) ? query.sessionType : null,
  };
};
